import { useEffect, useState } from 'react';
import { getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';

// TP-6: the 8-quarter daily Target-Profit report. Per game: target vs actual
// house profit for each 3-hour quarter + the day, with the PvH/PvB split.

async function apiFetch(url: string) {
  const t = getAdminAccessToken();
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(t ? { authorization: `Bearer ${t}` } : {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error?.message || 'API Error');
  return res.json();
}

// `pruned` (Phase 3, 2026-09-15): turnover was real but the rounds behind the
// profit have been deleted by the game's retention prune, so realizedPct is null
// because it is UNKNOWABLE — not because the quarter was quiet. Rendered 'n/a'.
type Cell = { quarter: number; label: string; turnover: number; profit: number; realizedPct: number | null; hit: boolean | null; pruned?: boolean; carve?: number; rebate?: number; netProfit?: number | null; netRealizedPct?: number | null };
type GameRep = { game: string; displayName: string; sessionType: 'pvh' | 'pvb'; targetPct: number | null; enabled: boolean; noTarget?: boolean; quarters: Cell[]; day: { turnover: number; profit: number; realizedPct: number | null; hit: boolean | null; pruned?: boolean; carve?: number; rebate?: number; netProfit?: number | null; netRealizedPct?: number | null } };
type Report = { date: string; quarters: number; includeTest?: boolean; games: GameRep[] };

const inr = (n: number) => `₹${(n | 0).toLocaleString('en-IN')}`;

function cellClasses(hit: boolean | null): string {
  if (hit === null) return 'text-neutral-500 dark:text-neutral-600';
  return hit
    ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10'
    : 'text-amber-700 dark:text-amber-400 bg-amber-500/10';
}

export default function TargetProfitReport() {
  const [date, setDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    setLoading(true); setErr('');
    try { setReport(await apiFetch(`/api/admin/reports/target-profit?date=${date}`)); }
    catch (e: any) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const qLabels = report?.games[0]?.quarters.map((q) => q.label) ?? [];

  return (
    <div className="max-w-6xl mx-auto p-4 text-gray-900 dark:text-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Target-Profit — Daily Report</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Per-game house profit vs target, across the 8 daily quarters (3 hours each, IST). Cricket excluded.
          </p>
          {report?.includeTest && (
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200"
                  title="The testing-phase switch on the Home page is ON: play from test accounts is counted here. Turn it off before launch.">
              Testing phase — counting test accounts
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
          <button onClick={load} className="px-3 py-2 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-500 text-white">
            {loading ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {err && <div className="mb-4 p-3 rounded-md bg-red-500/10 text-red-700 dark:text-red-400 text-sm">{err}</div>}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/60 text-left">
              <th className="p-2 sticky left-0 bg-gray-50 dark:bg-gray-800/60">Game</th>
              <th className="p-2 text-center">Target</th>
              {qLabels.map((l, i) => <th key={i} className="p-2 text-center whitespace-nowrap font-medium">{l}</th>)}
              <th className="p-2 text-center font-bold">DAY</th>
              <th className="p-2 text-center font-bold whitespace-nowrap" title="Gross take minus the agent chain's partnership share and the turnover rebate — the money the house actually keeps.">YOU KEEP</th>
            </tr>
          </thead>
          <tbody>
            {report?.games.map((g) => (
              <tr key={g.game} className="border-t border-gray-100 dark:border-gray-800">
                <td className="p-2 sticky left-0 bg-white dark:bg-gray-900">
                  <div className="font-semibold">{g.displayName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <span className={g.sessionType === 'pvh' ? 'text-sky-600 dark:text-sky-400' : 'text-violet-600 dark:text-violet-400'}>
                      {g.sessionType.toUpperCase()}
                    </span>
                    {' · '}{g.noTarget
                      ? <span className="text-neutral-500" title="The result is a declared number, not an engine take — there is no Target-Profit slider for this game. Its house profit is what the declared results actually produced.">no target</span>
                      : g.enabled ? <span className="text-emerald-600 dark:text-emerald-400">ON</span> : <span className="text-neutral-500">OFF</span>}
                  </div>
                </td>
                <td className="p-2 text-center font-bold text-indigo-600 dark:text-indigo-400">{g.targetPct == null ? <span className="opacity-50">—</span> : `${g.targetPct}%`}</td>
                {g.quarters.map((c) => (
                  <td key={c.quarter} className={`p-2 text-center ${cellClasses(c.hit)}`}
                      title={c.pruned
                        ? `${inr(c.turnover)} wagered — but this game's round history has been deleted by its retention prune, so the house profit for this quarter can no longer be read. This is NOT a zero take.`
                        : `${inr(c.turnover)} wagered · gross ${inr(c.profit)} · agents ${(c.carve ?? 0) >= 0 ? '−' : '+'}${inr(Math.abs(c.carve ?? 0))} · rebate −${inr(c.rebate ?? 0)} · you keep ${inr(c.netProfit ?? 0)}`}>
                    {c.realizedPct == null
                      ? (c.pruned
                          ? <span className="text-sky-600 dark:text-sky-400" title="history pruned">n/a</span>
                          : <span className="opacity-50">—</span>)
                      : <span className="font-semibold">{c.realizedPct}%</span>}
                  </td>
                ))}
                <td className={`p-2 text-center font-bold ${cellClasses(g.day.hit)}`}
                    title={g.day.pruned
                      ? `${inr(g.day.turnover)} wagered — at least one quarter's round history has been pruned, so the day's realised take cannot be stated.`
                      : `${inr(g.day.turnover)} wagered · house ${inr(g.day.profit)}`}>
                  {g.day.realizedPct == null
                    ? (g.day.pruned
                        ? <span className="text-sky-600 dark:text-sky-400">n/a</span>
                        : <span className="opacity-50">—</span>)
                    : `${g.day.realizedPct}%`}
                </td>
                <td className="p-2 text-center font-bold whitespace-nowrap"
                    title={g.day.pruned
                      ? 'The gross take for at least one quarter is unreadable (history pruned), so what you keep cannot be stated either — the agent carve is still on record, but subtracting it from an unknown gross would invent a number.'
                      : `gross ${inr(g.day.profit)} − agents ${inr(g.day.carve ?? 0)} − rebate ${inr(g.day.rebate ?? 0)}`}>
                  {g.day.netProfit == null
                    ? <span className="text-sky-600 dark:text-sky-400">n/a</span>
                    : <span className={g.day.netProfit >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}>
                        {inr(g.day.netProfit)}
                        {g.day.netRealizedPct != null && <span className="opacity-60 font-normal"> ({g.day.netRealizedPct}%)</span>}
                      </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>Each cell = realized house profit as % of user bets that quarter. <span className="text-emerald-600 dark:text-emerald-400">Green</span> = on target (±3), <span className="text-amber-600 dark:text-amber-400">amber</span> = off target, — = no activity, <span className="text-sky-600 dark:text-sky-400">n/a</span> = money was wagered but this game's round history has since been deleted by its retention prune, so the take is <b>not knowable</b> (it is NOT a zero take). Hover a cell for the details.</p>
        <p>Retention is per game: lucky7 and thimbles keep 10 days of rounds, roulette/casino/aviator/andar bahar/teen patti 30, plinko is not pruned. From 2026-09-15 every settled round also writes a durable rollup row, so dates from then on stay readable permanently and <span className="text-sky-600 dark:text-sky-400">n/a</span> should only ever appear on older dates.</p>
        <p><b>YOU KEEP</b> = gross take − the agent chain's partnership share − the turnover rebate. It is what reaches the house after everyone else is paid, and it is the number to judge the business on; the % beside it is that over the day's turnover. The per-quarter cells stay GROSS because that is what the Target profit % dial actually steers. Note the agent share can be NEGATIVE on a losing day — a partnership shares losses too — so "you keep" can exceed gross.</p>
        <p><b>PVH</b> = Player-vs-Home casino games · <b>PVB</b> = Player-vs-Bot games (only bot matches are governed). Fees are excluded (§8).</p>
      </div>
    </div>
  );
}
